import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getPlans, createCheckoutSession, PlanOption } from '../services/billingService';

/**
 * Pricing — Free / Plus / Teams. Upgrade kicks off Stripe Checkout once payments
 * are configured; until then it shows a graceful "coming soon" note.
 */
const Pricing: React.FC = () => {
  const { user, signInWithGoogle } = useAuth();
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    getPlans().then((r) => setPlans(r.plans)).catch(() => setPlans([])).finally(() => setLoading(false));
  }, []);

  const onUpgrade = async (plan: PlanOption) => {
    setNote('');
    if (plan.id === 'free') return;
    if (!user) { try { await signInWithGoogle(); } catch { /* user dismissed */ } return; }
    setBusy(plan.id);
    try {
      const r = await createCheckoutSession(plan.id);
      if (r.configured && r.url) window.location.href = r.url;
      else setNote(r.message || "Checkout isn't available yet.");
    } catch (e: any) {
      setNote(e?.message || 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      <header className="max-w-5xl mx-auto px-6 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-secondary-500 hover:text-secondary-900 dark:hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 pb-16">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Plans for creators</h1>
          <p className="mt-2 text-secondary-500 dark:text-secondary-400">Make and publish for free; credits come from top-ups and never expire. Subscribe when you are ready to charge for your episodes — the plan is a seller licence, not a bundle of credits.</p>
        </div>

        {note && (
          <p className="mb-6 text-center text-sm text-secondary-600 dark:text-secondary-300 bg-secondary-100 dark:bg-secondary-900 rounded-lg py-2 px-4 max-w-xl mx-auto">{note}</p>
        )}

        {/* Loading + empty fallbacks so the page never dead-ends when plans haven't loaded (offline/backend down). */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" aria-hidden>
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-72 rounded-2xl border border-secondary-200 dark:border-secondary-700 animate-pulse bg-secondary-50 dark:bg-secondary-900" />
            ))}
          </div>
        )}
        {!loading && plans.length === 0 && (
          <div className="text-center max-w-md mx-auto py-10">
            <p className="text-secondary-600 dark:text-secondary-300">Plans are taking a moment to load.</p>
            <button onClick={() => window.location.reload()} className="mt-4 inline-flex items-center justify-center px-5 py-2.5 rounded-lg border border-secondary-300 dark:border-secondary-700 text-sm font-medium hover:bg-secondary-50 dark:hover:bg-secondary-800">Retry</button>
            <p className="mt-4 text-sm text-secondary-500"><Link to="/" className="underline">Start free</Link> — no plan needed to begin.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((plan) => (
            <div
              key={plan.id}
              data-testid={`plan-${plan.id}`}
              className={`rounded-2xl border p-6 flex flex-col ${
                plan.highlighted
                  ? 'border-secondary-900 dark:border-white shadow-lg'
                  : 'border-secondary-200 dark:border-secondary-700'
              }`}
            >
              <div className="mb-4">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-lg font-semibold">{plan.label}</h2>
                  {plan.highlighted && (
                    <span className="text-[11px] font-medium uppercase tracking-wide rounded-full bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 px-2 py-0.5">Popular</span>
                  )}
                </div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">{plan.tagline}</p>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{plan.priceMonthlyUsd === 0 ? 'Free' : `$${plan.priceMonthlyUsd}`}</span>
                  {plan.priceMonthlyUsd > 0 && (
                    <span className="text-sm text-secondary-400">/{plan.perSeat ? 'seat·mo' : 'mo'}</span>
                  )}
                </div>
              </div>

              <ul className="flex-1 space-y-2 mb-6">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-secondary-700 dark:text-secondary-200">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-secondary-900 dark:text-white" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => onUpgrade(plan)}
                disabled={plan.id === 'free' || busy === plan.id}
                className={`w-full rounded-xl px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                  plan.highlighted
                    ? 'bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100'
                    : 'border border-secondary-300 dark:border-secondary-600 text-secondary-800 dark:text-secondary-100 hover:bg-secondary-100 dark:hover:bg-secondary-800'
                }`}
              >
                {busy === plan.id ? 'Starting…' : !user && plan.id !== 'free' ? `Sign in to ${plan.cta.toLowerCase()}` : plan.cta}
              </button>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Pricing;
